import { cn } from "@/components/utils/utils";

/**
 * Tono della pillola: colora solo il pallino, ricalcando ItineraryStatusBadge, cosi' il
 * filtro e i badge sulle card parlano la stessa lingua. Scritta e pillola attiva hanno lo
 * stesso aspetto per tutti gli stati.
 */
export type StatusFilterTone =
  | "neutral"
  | "draft"
  | "archived"
  | "sent"
  | "changes"
  | "installments"
  | "confirmed"
  | "requestNew"
  | "requestToAssign"
  | "requestInProgress"
  | "requestImminent";

export interface StatusFilterOption<T extends string> {
  value: T;
  label: string;
  /** Assente = nessun numero sulla pillola. */
  count?: number;
  tone?: StatusFilterTone;
}

interface StatusFilterProps<T extends string> {
  options: readonly StatusFilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Etichetta davanti alle pillole, letta anche dagli screen reader. */
  label: string;
  className?: string;
}

const TONE_DOTS: Record<StatusFilterTone, string> = {
  neutral: "bg-gray-400",
  draft: "bg-gray-400",
  // expired / rejected
  archived: "bg-red-400",
  // sended
  sent: "bg-yellow-400",
  // request_changes: il badge e' bianco con bordo grigio
  changes: "bg-gray-300",
  // installments_in_progress
  installments: "bg-amber-400",
  // completed / upcoming / ongoing
  confirmed: "bg-green-500",
  // Richieste create negli ultimi 7 giorni
  requestNew: "bg-sky-400",
  // Richieste senza un membro che ci lavori: ambra come il badge "Da assegnare"
  requestToAssign: "bg-amber-400",
  // Richieste assigned: grigio come il badge "Assegnata"
  requestInProgress: "bg-gray-500",
  // Richieste in partenza entro 30 giorni
  requestImminent: "bg-rose-400",
};

/**
 * Filtro a scelta singola sugli stati di una lista.
 *
 * Volutamente diverso da Button e Select: e' un filtro, non un'azione. Da inattive le
 * pillole hanno il bordo tratteggiato e nessun riempimento; l'attiva prende una tinta del
 * colore primario, uguale per ogni stato: il colore dello stato sta solo nel pallino.
 * L'etichetta in testa dice di cosa sono le pillole.
 */
export const StatusFilter = <T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: StatusFilterProps<T>) => (
  <div
    role="radiogroup"
    aria-label={label}
    className={cn("flex flex-wrap items-center gap-1.5", className)}
  >
    <span aria-hidden className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
      {label}
    </span>
    {options.map((option) => {
      const selected = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            selected
              ? "border border-primary/30 bg-primary/10 text-gray-900"
              : "border border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700"
          )}
        >
          <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", TONE_DOTS[option.tone ?? "neutral"])} />
          {option.label}
          {option.count !== undefined && (
            <span
              className={cn(
                "min-w-[1.25rem] rounded-full px-1 text-center text-[10px] tabular-nums",
                selected ? "bg-primary text-primary-foreground" : "bg-gray-100 text-gray-500"
              )}
            >
              {option.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);
